/**
 * Which of the room's documents to offer first when someone links a document
 * that is ALREADY in the data room to a checklist item (owner ask 2026-10-15:
 * "there is no way to link them to a doc already in the data room").
 *
 * Pure and shared, so the admin checklist and the seller's checklist order the
 * picker the same way. It reads only what the item and the document listing
 * already carry — the item's category and the period in its title, the
 * document's categories, `dataDate` and `period` — and never widens what the
 * caller may see: the candidates are exactly the documents the caller's own
 * listing returned.
 *
 * "Likely" means: filed to the item's category, or (when the item names a
 * period) dated in that period while not filed to a different category. Within
 * the likely group a document that matches both comes first. Everything else
 * follows, by name. A document already linked to the item is never offered.
 */
import type { DdRequest, RoomDocument } from '../api/types';

/** The period an item's title names, as the template expands it (ADR-0040):
 * "2025", "FY2025", "2025 Q4", "December 2025", "FY2026 YTD". */
export type ItemPeriod =
  | { kind: 'year'; year: number }
  | { kind: 'quarter'; year: number; quarter: number; fiscal: boolean }
  | { kind: 'month'; year: number; month: number };

const MONTH_NAMES = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/** The period named in an item title, or null when it names none. */
export function itemPeriod(title: string): ItemPeriod | null {
  const month = title.match(
    /\b(January|February|March|April|May|June|July|August|September|October|November|December)\s+((?:19|20)\d{2})\b/i,
  );
  if (month) {
    const idx = MONTH_NAMES.indexOf(month[1]!.toLowerCase() as (typeof MONTH_NAMES)[number]);
    return { kind: 'month', year: Number(month[2]), month: idx + 1 };
  }
  const quarter = title.match(/\b(FY)?((?:19|20)\d{2})\s+Q([1-4])\b/i);
  if (quarter) {
    return {
      kind: 'quarter',
      year: Number(quarter[2]),
      quarter: Number(quarter[3]),
      fiscal: Boolean(quarter[1]),
    };
  }
  const year = title.match(/\b(?:FY)?((?:19|20)\d{2})\b/i);
  if (year) return { kind: 'year', year: Number(year[1]) };
  return null;
}

/** Whether a document's date of data falls in the item's period. A fiscal
 * quarter's months depend on the room's year end, so it matches on the year. */
export function documentInPeriod(
  doc: Pick<RoomDocument, 'dataDate'>,
  period: ItemPeriod,
): boolean {
  const m = doc.dataDate?.match(/^(\d{4})-(\d{2})/);
  if (!m) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  if (year !== period.year) return false;
  if (period.kind === 'month') return month === period.month;
  if (period.kind === 'quarter' && !period.fiscal) {
    return Math.ceil(month / 3) === period.quarter;
  }
  return true;
}

/** Whether the document is filed to the item's category: its category ids
 * include the item's, or (when the listing carries no ids) its filing path
 * starts with the item's path. */
export function documentInItemCategory(
  doc: Pick<RoomDocument, 'categoryTagIds' | 'categoryPath'>,
  req: Pick<DdRequest, 'categoryTagId' | 'categoryPath'>,
): boolean {
  if (req.categoryTagId && doc.categoryTagIds?.includes(req.categoryTagId)) return true;
  const itemPath = Array.isArray(req.categoryPath) ? req.categoryPath : [];
  const docPath = Array.isArray(doc.categoryPath) ? doc.categoryPath : [];
  if (itemPath.length === 0 || docPath.length < itemPath.length) return false;
  return itemPath.every((name, i) => docPath[i] === name);
}

export interface LinkCandidates {
  /** Filed to the item's category and/or dated in its period — offered first. */
  likely: RoomDocument[];
  /** Every other document the caller can see. */
  others: RoomDocument[];
}

/** Lower-cased, so the search box matches regardless of case. */
function norm(s: string): string {
  return s.toLocaleLowerCase();
}

/**
 * The documents to offer for linking to `req`, likely matches first. `query`
 * narrows both groups by document name (case-insensitive substring). Already
 * linked documents are left out.
 */
export function rankLinkCandidates(
  req: Pick<DdRequest, 'title' | 'categoryTagId' | 'categoryPath' | 'linkedDocumentIds'>,
  documents: readonly RoomDocument[],
  query = '',
): LinkCandidates {
  const linked = new Set(req.linkedDocumentIds);
  const period = itemPeriod(req.title);
  const q = norm(query.trim());
  const scored = documents
    .filter((d) => !linked.has(d.id))
    .filter((d) => (q ? norm(d.name).includes(q) : true))
    .map((doc) => {
      const inCategory = documentInItemCategory(doc, req);
      const inPeriod = period !== null && documentInPeriod(doc, period);
      const filedElsewhere =
        !inCategory && (doc.categoryPath?.length ?? 0) > 0 && (req.categoryPath?.length ?? 0) > 0;
      const likely = inCategory || (inPeriod && !filedElsewhere);
      return { doc, likely, score: (inCategory ? 2 : 0) + (inPeriod ? 1 : 0) };
    });
  const byName = (a: RoomDocument, b: RoomDocument) => a.name.localeCompare(b.name);
  const likely = scored
    .filter((s) => s.likely)
    .sort((a, b) => b.score - a.score || byName(a.doc, b.doc))
    .map((s) => s.doc);
  const others = scored
    .filter((s) => !s.likely)
    .map((s) => s.doc)
    .sort(byName);
  return { likely, others };
}
